/**
 * E07-F04-T12 — "Information collected <date> — refresh?" next to a company that an
 * EARLIER research request already checked.
 *
 * Owner decision 2026-09-24 (docs/research/examples-analysis.md §3, row 4): research
 * runs in rounds; a company found again stays in the new request's list, marked with
 * the date its data was collected and a link to that earlier request. "Refresh" is a
 * new collection for THIS company only and fires ONLY from the click on the button —
 * the AI check is paid (no-automatic-AI policy). Nothing here runs on render.
 *
 * Presentational: the server projects the mark (`priorCollection` on pipeline and
 * review rows, shared `projectPriorCollection`); the host screen owns the mutation
 * (POST …/rerun-ai) and the navigation to the earlier request.
 */
import type { JSX } from 'react';
import {
  PRIOR_COLLECTION_REFRESH_HINT,
  priorCollectionDate,
  priorCollectionLabel,
  type PriorCollection,
} from '@eer/shared';

export interface PriorCollectionNoteProps {
  readonly rowId: number;
  readonly prior: PriorCollection | null | undefined;
  /** PAID — re-collect this company's data. Called only from the button's click. */
  readonly onRefresh: () => void;
  /** Disable the button (another run is being started / no website). */
  readonly refreshDisabled?: boolean;
  /** Why the button is disabled (tooltip), when it is. */
  readonly refreshDisabledReason?: string;
  /** Open the earlier request. Without it the request is shown as plain text. */
  readonly onOpenRequest?: (requestId: number, title: string) => void;
}

/** The text of the "where from" line (pure, exported for tests). */
export function priorCollectionSourceText(pc: PriorCollection): string {
  const verdict =
    pc.grade != null || pc.score != null
      ? ` (${[pc.grade, pc.score != null ? `${pc.score}%` : null].filter(Boolean).join(', ')})`
      : '';
  return `${pc.requestTitle || `request #${pc.requestId}`}${verdict}`;
}

export function PriorCollectionNote({
  rowId,
  prior,
  onRefresh,
  refreshDisabled = false,
  refreshDisabledReason,
  onOpenRequest,
}: PriorCollectionNoteProps): JSX.Element | null {
  if (!prior) return null;
  const sourceText = priorCollectionSourceText(prior);
  return (
    <div
      data-testid={`prior-collection-${rowId}`}
      data-reused={prior.reused ? 'true' : 'false'}
      data-refreshing={prior.refreshing ? 'true' : 'false'}
      data-collected-at={priorCollectionDate(prior.collectedAt)}
      className="mt-1 flex max-w-sm flex-wrap items-center gap-x-1.5 gap-y-0.5 rounded border border-sky-200 bg-sky-50 px-1.5 py-0.5 text-[11px] text-sky-800"
    >
      <i className="fa-solid fa-clock-rotate-left" aria-hidden />
      <span data-testid={`prior-collection-label-${rowId}`}>{priorCollectionLabel(prior)}</span>
      <span className="text-sky-700">
        from{' '}
        {onOpenRequest ? (
          <button
            type="button"
            data-testid={`prior-collection-link-${rowId}`}
            data-request-id={prior.requestId}
            onClick={() => onOpenRequest(prior.requestId, prior.requestTitle)}
            title="Open the earlier request"
            className="font-semibold underline hover:text-sky-950"
          >
            {sourceText}
          </button>
        ) : (
          <span data-testid={`prior-collection-link-${rowId}`} className="font-semibold">
            {sourceText}
          </span>
        )}
      </span>
      {!prior.reused ? (
        <span
          data-testid={`prior-collection-not-reused-${rowId}`}
          className="text-sky-700"
          title="This request checks different criteria, so the earlier verdict is shown for reference only"
        >
          · criteria differ, not carried over
        </span>
      ) : null}
      {prior.refreshing ? (
        <span data-testid={`prior-collection-refreshing-${rowId}`} className="font-semibold">
          <i className="fa-solid fa-spinner fa-spin mr-1" aria-hidden />
          Refreshing…
        </span>
      ) : (
        <button
          type="button"
          data-testid={`prior-collection-refresh-${rowId}`}
          disabled={refreshDisabled}
          onClick={onRefresh}
          title={
            refreshDisabled && refreshDisabledReason
              ? refreshDisabledReason
              : PRIOR_COLLECTION_REFRESH_HINT
          }
          className="rounded border border-sky-300 bg-white px-1.5 py-px font-semibold text-sky-800 hover:bg-sky-100 disabled:opacity-40"
        >
          <i className="fa-solid fa-rotate-right mr-1" aria-hidden />
          Refresh
        </button>
      )}
    </div>
  );
}

export default PriorCollectionNote;
